// Port of manager/static/collect/collector.js — orchestrates the probe set
// and posts the payload to /api/collect/fingerprint. UI rendering and the
// progress callback are kept extension points so the same module powers
// both the standalone collection page and an embeddable SDK.

import * as P from './probes.js';

export interface CollectOptions {
  workerUrl: string;                       // base URL of the FingerprintCLF Worker
  turnstileToken?: string;                 // injected by the Turnstile widget
  onProgress?: (step: number, total: number, label: string) => void;
  siteKey?: string;                        // SDK consumer identifier
  referrer?: string;                       // override (defaults to document.referrer)
  source?: string;                         // free-form tag — origin of the visit (utm/campaign/etc)
}

export interface ServerResponse {
  id?: string;
  score: number;
  promoted?: boolean;
  eligible?: boolean;
  ineligible_reasons?: string[];
  details?: Record<string, string>;
  detail?: string;                         // error message from the Worker
}

export interface CollectResult {
  fingerprint: Record<string, unknown>;
  server: ServerResponse;
}

const STEP_LABELS = [
  'Checking navigator properties...',
  'Probing WebGL capabilities...',
  'Analyzing WebGL2 support...',
  'Probing WebGPU adapter...',
  'Generating canvas signature...',
  'Testing audio pipeline...',
  'Enumerating speech voices...',
  'Reading screen properties...',
  'Requesting client hints...',
  'Detecting available fonts...',
  'Reading timezone...',
  'Inspecting performance memory & connection...',
  'Enumerating plugins & media queries...',
  'Querying permissions & battery & media devices...',
  'Reading window & orientation...',
  'Fingerprinting advanced signals (WebGL image, math, fonts, emoji)...',
  'Submitting results...',
];

const TOTAL_STEPS = STEP_LABELS.length;

// Spread probes over time; matches collector.js (50-200ms jitter) so timing
// patterns aren't a fingerprint themselves.
function delayed<T>(fn: () => T | Promise<T>): Promise<T | null> {
  return new Promise((resolve) => {
    const wait = 50 + Math.floor(Math.random() * 150);
    setTimeout(() => {
      try {
        const out = fn();
        if (out && typeof (out as Promise<T>).then === 'function') {
          (out as Promise<T>).then(resolve, () => resolve(null));
        } else {
          resolve(out as T);
        }
      } catch {
        resolve(null);
      }
    }, wait);
  });
}

export function extractBrowserVersion(ua: string): string {
  if (!ua) return '';
  let m = ua.match(/(?:Chrome|CriOS)\/(\d+[\d.]*)/);
  if (m && m[1]) return m[1];
  m = ua.match(/Firefox\/(\d+[\d.]*)/);
  if (m && m[1]) return m[1];
  m = ua.match(/Version\/(\d+[\d.]*).*Safari/);
  if (m && m[1]) return m[1];
  m = ua.match(/Edge\/(\d+[\d.]*)/);
  if (m && m[1]) return m[1];
  return '';
}

export function parseBrowserName(ua: string): string {
  if (!ua) return 'Unknown';
  if (/Edg\//.test(ua)) return 'Microsoft Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/Brave/.test(ua)) return 'Brave';
  if (/Chrome|CriOS/.test(ua)) return 'Google Chrome';
  if (/Firefox|FxiOS/.test(ua)) return 'Mozilla Firefox';
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Apple Safari';
  return 'Unknown';
}

export function parseOS(ua: string): string {
  if (!ua) return 'Unknown';
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
  if (/Windows NT 6\.3/.test(ua)) return 'Windows 8.1';
  if (/Windows NT 6\.2/.test(ua)) return 'Windows 8';
  if (/Windows NT 6\.1/.test(ua)) return 'Windows 7';
  if (/Windows/.test(ua)) return 'Windows';
  const macMatch = ua.match(/Mac OS X (\d+[._]\d+)/);
  if (macMatch && macMatch[1]) return `macOS ${macMatch[1].replace(/_/g, '.')}`;
  if (/Macintosh/.test(ua)) return 'macOS';
  const androidMatch = ua.match(/Android (\d+)/);
  if (androidMatch && androidMatch[1]) return `Android ${androidMatch[1]}`;
  if (/Linux/.test(ua)) return 'Linux';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/CrOS/.test(ua)) return 'Chrome OS';
  return 'Unknown';
}

export async function runCollection(opts: CollectOptions): Promise<CollectResult> {
  const fp: Record<string, unknown> = {};
  const onProgress = opts.onProgress ?? (() => undefined);
  const step = (i: number) => onProgress(i, TOTAL_STEPS, STEP_LABELS[i - 1] ?? '');

  try { fp['environment'] = P.probeEnvironment(); }
  catch (e) { fp['environment'] = { error: errMsg(e) }; }

  step(1);  fp['navigator'] = await delayed(P.probeNavigator);

  step(2);  const webgl1 = (await delayed(P.probeWebGL1)) as Record<string, unknown> | null;
  step(3);  const webgl2 = (await delayed(P.probeWebGL2)) as Record<string, unknown> | null;
  fp['webgl'] = {
    vendor: (webgl1 && webgl1['vendor']) ?? '',
    renderer: (webgl1 && webgl1['renderer']) ?? '',
    params: (webgl1 && webgl1['params']) ?? {},
    extensions: (webgl1 && webgl1['extensions']) ?? [],
    params2: (webgl2 && webgl2['params2']) ?? {},
    extensions2: (webgl2 && webgl2['extensions2']) ?? [],
    status1: webgl1 && webgl1['status'],
    attempts1: webgl1 && webgl1['attempts'],
    status2: webgl2 && webgl2['status'],
    attempts2: webgl2 && webgl2['attempts'],
  };

  step(4);  fp['webgpu'] = await delayed(() => P.probeWebGPU());

  step(5);  fp['canvasHash'] = await delayed(P.probeCanvas);

  step(6);
  const audio = (await delayed(() => P.probeAudio())) as Record<string, unknown> | null;
  fp['audioHash'] = (audio && audio['hash']) ?? '';
  fp['audioContext'] = audio
    ? {
      sampleRate: audio['sampleRate'],
      channelCount: audio['channelCount'],
      maxChannelCount: audio['maxChannelCount'],
      state: audio['state'],
      status: audio['status'],
      supported: audio['supported'],
    }
    : null;

  step(7);  fp['voices'] = await delayed(() => P.probeVoices());
  step(8);  fp['screen'] = await delayed(P.probeScreen);
  step(9);  fp['clientHints'] = await delayed(() => P.probeClientHints());
  step(10); fp['fonts'] = await delayed(P.probeFonts);
  step(11); fp['timezone'] = await delayed(P.probeTimezone);

  step(12);
  fp['performanceMemory'] = await delayed(P.probePerformanceMemory);
  fp['connection'] = await delayed(P.probeConnection);

  step(13);
  fp['plugins'] = await delayed(P.probePlugins);
  fp['prefers'] = await delayed(P.probeMediaQueries);
  fp['uaChLowEntropy'] = await delayed(P.probeUACHLowEntropy);

  step(14);
  const asyncTrio = await delayed(() => Promise.all([
    P.probePermissions(),
    P.probeBattery(),
    P.probeMediaDevices(),
  ]));
  fp['permissions'] = (asyncTrio && asyncTrio[0]) ?? null;
  fp['battery'] = (asyncTrio && asyncTrio[1]) ?? null;
  fp['mediaDevices'] = (asyncTrio && asyncTrio[2]) ?? null;

  step(15);
  fp['windowSize'] = await delayed(P.probeWindowSize);
  fp['orientation'] = await delayed(P.probeOrientation);

  step(16);
  fp['webglImageHash'] = await delayed(P.probeWebGLImageHash);
  fp['mathSignature'] = await delayed(P.probeMathSignature);
  fp['fontMetrics'] = await delayed(P.probeFontMetrics);
  fp['emojiHash'] = await delayed(P.probeEmojiHash);

  const ua = (fp['navigator'] as { userAgent?: string } | null)?.userAgent ?? '';
  fp['browserVersion'] = extractBrowserVersion(ua);
  fp['_siteKey'] = opts.siteKey ?? '';
  fp['_referrer'] = opts.referrer ?? document.referrer ?? '';
  fp['_source'] = (opts.source ?? '').slice(0, 64);
  if (opts.turnstileToken) {
    fp['cf-turnstile-response'] = opts.turnstileToken;
  }

  step(17);
  let server: ServerResponse;
  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (opts.turnstileToken) headers['cf-turnstile-response'] = opts.turnstileToken;
    const url = new URL('/api/collect/fingerprint', opts.workerUrl).toString();
    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(fp),
      mode: 'cors',
      credentials: 'omit',
    });
    server = (await resp.json()) as ServerResponse;
    if (!resp.ok && server.score === undefined) {
      server.score = 0;
    }
  } catch (e) {
    server = { score: 0, detail: errMsg(e) };
  }

  return { fingerprint: fp, server };
}

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return String(e);
}
